/**
 * Public verification projection of ONE coupon (design V5, panel v2 2026-10-24).
 *
 * Pure functions only: the database reads live in `store-history.ts`. Three kinds of evidence, and nothing else:
 *   - verification runs (VerificationRun), conclusive ones only (`runOutcome`);
 *   - valid shopper reports (Vote, after the bot filter of `valid-votes.ts`). Our browser extension's opt-in
 *     "worked / didn't work" result is stored as a Vote too (`/api/extension/result`), so it is a shopper report here;
 *   - code usage (CouponClick, human clicks only: `human-clicks.ts`), last 180 days.
 *
 * Card (server HTML, ISR): ONE compact line "Verified 14h ago · Used 19h ago", each fragment present only when its
 * data exists (`cardFragments`); no fragment -> no line. A merchant-confirmed offer (Coupon.protectedFromExpiry, CODE or
 * DEAL; owner 2026-10-24) with no VALID checker run leads the line with "Confirmed 2h ago" instead of "Verified ...". The success percentage is NOT repeated there: the card header
 * already shows it as "X% Success" (coordinator 2026-10-24: no number twice on a card). Panel (fetched on first click from
 * `/api/coupons/[id]/history`): stats "Total uses" / "Verifications" / "Last used" and a "Verification activity"
 * timeline, newest first, with real actors only (`CouponActivity`). A merchant-confirmed offer has one more entry,
 * "Confirmed on contabo.com", dated with the protection date (Coupon.protectedAt); when that date is unknown the entry
 * has no time (never a guessed one).
 *
 * Deliberately NOT public: checker/tool names, raw verdict codes, run triggers, evidence/trace URLs (none exist in the
 * database anyway), error text, durations, IP addresses, session ids, e-mails, and every inconclusive or
 * infrastructure verdict (NOT_APPLICABLE, MIN_NOT_MET, BLOCKED, ERROR, ...): those are not "the code doesn't work".
 * A user is named only by the short public form the community leaderboard already shows ("Maria P."), else
 * "A shopper".
 */
import { VERIFIER_DISPLAY_NAME } from "./site-config";

export { VERIFIER_DISPLAY_NAME };

/** Window of the usage stats (human clicks): the same 180 days as the valid shopper reports (`valid-votes.ts`). */
export const USAGE_WINDOW_DAYS = 180;
/** Timeline entries returned by the history endpoint. */
export const ACTIVITY_MAX = 20;
/** Timeline rows shown before "Show N more" (the rest are already loaded: no second fetch). */
export const ACTIVITY_VISIBLE = 5;

export type HistoryOutcome = "worked" | "failed" | "expired";

export interface LastCheck {
    /** ISO time of the newest conclusive run. */
    date: string;
    outcome: HistoryOutcome;
}

/** What the server renders per coupon (a summary, never the event list). */
export interface CouponHistory {
    couponId: string;
    /** Conclusive runs in the database for this coupon. */
    verifications: number;
    /** Newest conclusive run; null when there is none (no "Verified" fragment then). */
    lastCheck: LastCheck | null;
    /** Valid shopper reports (one per identity, bots excluded, 180 days). */
    shopperWorked: number;
    shopperFailed: number;
    /** Newest valid UP vote (ISO). Feeds the ranking tie-break only. */
    lastUpVoteAt: string | null;
    /** Days in the last 180 with at least one human use of the code. */
    usageDays: number;
    /** Newest human use of the code in the last 180 days (ISO); null / absent without one. */
    lastUsedAt?: string | null;
    /**
     * Human uses in the last 180 days, one per shopper per day: the same number as the panel's "Total uses" tile
     * (`ActivityStats.uses`). The card header's "Used N times" reads it on the store page.
     */
    uses?: number;
    /** The offer is merchant-confirmed (Coupon.protectedFromExpiry): it has a "Confirmed" history entry from day one. */
    confirmed?: boolean;
    /** When the confirmation began (ISO, Coupon.protectedAt); null / absent = unknown (no time is shown, none is invented). */
    confirmedAt?: string | null;
    /** At least one conclusive run, one valid report, one day of usage or a merchant confirmation: the "History" button is rendered only then. */
    hasHistory: boolean;
}

export interface RunRow {
    id: string;
    verdict: string;
    appliedEffect?: string | null;
    createdAt: Date | string;
}

function toDate(value: Date | string | null | undefined): Date | null {
    if (value === null || value === undefined || value === "") return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Outcome a run may show publicly, or null when the run must stay internal.
 * VALID is a success. EXPIRED / INVALID count only when the system acted on it (appliedEffect EXPIRED_COUPON): an
 * unconfirmed or flagged-for-review failure is not a fact the shopper should be told.
 */
export function runOutcome(run: { verdict: string; appliedEffect?: string | null }): HistoryOutcome | null {
    if (run.verdict === "VALID") return "worked";
    if (run.appliedEffect === "EXPIRED_COUPON") {
        if (run.verdict === "EXPIRED") return "expired";
        if (run.verdict === "INVALID") return "failed";
    }
    return null;
}

export interface BuildHistoryInput {
    couponId: string;
    /** Newest conclusive runs, any order (the query may limit them to the newest one). */
    runs: RunRow[];
    /** Total conclusive runs in the database for this coupon (>= conclusive runs given). */
    runTotal?: number;
    /** Valid votes (after the bot filter). */
    voteUp?: number;
    voteDown?: number;
    lastUpVoteAt?: Date | string | null;
    /** Days with human usage in the last 180 days. */
    usageDays?: number;
    /** Newest human use in the last 180 days. */
    lastUsedAt?: Date | string | null;
    /** Human uses in the last 180 days, one per shopper per day. */
    uses?: number;
    /** Merchant-confirmed offer (Coupon.protectedFromExpiry). */
    confirmed?: boolean;
    /** Coupon.protectedAt. */
    confirmedAt?: Date | string | null;
}

export function buildCouponHistory(input: BuildHistoryInput): CouponHistory {
    let lastCheck: LastCheck | null = null;
    let conclusive = 0;
    for (const run of input.runs) {
        const outcome = runOutcome(run);
        const when = toDate(run.createdAt);
        if (!outcome || !when) continue;
        conclusive++;
        const iso = when.toISOString();
        if (!lastCheck || iso > lastCheck.date) lastCheck = { date: iso, outcome };
    }
    const verifications = Math.max(input.runTotal ?? 0, conclusive);
    const shopperWorked = Math.max(0, input.voteUp ?? 0);
    const shopperFailed = Math.max(0, input.voteDown ?? 0);
    const usageDays = Math.max(0, input.usageDays ?? 0);
    const lastUsedAt = usageDays > 0 ? toDate(input.lastUsedAt)?.toISOString() ?? null : null;
    const confirmed = input.confirmed === true;
    return {
        couponId: input.couponId,
        verifications,
        // A check exists only with a real run: Coupon.lastVerifiedAt is not a run.
        lastCheck: verifications > 0 ? lastCheck : null,
        shopperWorked,
        shopperFailed,
        lastUpVoteAt: toDate(input.lastUpVoteAt)?.toISOString() ?? null,
        usageDays,
        lastUsedAt,
        uses: usageDays > 0 ? Math.max(0, Math.floor(input.uses ?? 0)) : 0,
        confirmed,
        confirmedAt: confirmed ? toDate(input.confirmedAt)?.toISOString() ?? null : null,
        hasHistory: verifications > 0 || shopperWorked + shopperFailed > 0 || usageDays > 0 || confirmed,
    };
}

export interface CardFragments {
    /** Newest conclusive run, only when that run WORKED (a newer failure is never shown as "Verified"). */
    verifiedAt: string | null;
    /** Newest human use (180 days). */
    usedAt: string | null;
    /**
     * Merchant-confirmed offer with NO worked checker run (a checker "Verified ..." takes the place of it): the first
     * fragment reads "Confirmed 2h ago", or "Confirmed by the merchant" when the date is unknown (`confirmedAt` null).
     */
    confirmed: boolean;
    confirmedAt: string | null;
}

/** The pieces of the card's one line. All empty -> the card renders no line at all. */
export function cardFragments(history: Partial<Pick<CouponHistory, "lastCheck" | "lastUsedAt" | "confirmed" | "confirmedAt">> | undefined): CardFragments {
    if (!history) return { verifiedAt: null, usedAt: null, confirmed: false, confirmedAt: null };
    const check = history.lastCheck;
    const verifiedAt = check && check.outcome === "worked" && toDate(check.date) ? check.date : null;
    const confirmed = history.confirmed === true && !verifiedAt;
    return {
        verifiedAt,
        usedAt: toDate(history.lastUsedAt)?.toISOString() ?? null,
        confirmed,
        confirmedAt: confirmed ? toDate(history.confirmedAt)?.toISOString() ?? null : null,
    };
}

export function hasCardLine(f: CardFragments): boolean {
    return !!(f.verifiedAt || f.usedAt || f.confirmed);
}

// ---------------------------------------------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct 8, 2026" (UTC, locale independent, identical on server and client). */
export function formatHistoryDate(value: Date | string | null | undefined): string | null {
    const d = toDate(value);
    if (!d) return null;
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** "Oct 8" (UTC): the server fallback of every relative time (no year, no clock). */
export function formatMonthDay(value: Date | string | null | undefined): string | null {
    const d = toDate(value);
    return d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}` : null;
}

/** "Oct 8, 2026, 14:05 UTC": the tooltip of a <time>. */
export function formatAbsolute(value: Date | string | null | undefined): string | null {
    const d = toDate(value);
    if (!d) return null;
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    return `${formatHistoryDate(d)}, ${hh}:${mm} UTC`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/** Beyond this many days a time is a date ("Oct 3"), not "N days ago". */
export const RELATIVE_MAX_DAYS = 30;

/**
 * "just now" / "5m ago" / "14h ago" / "yesterday" / "8 days ago" / "Oct 3" (beyond 30 days) / "Oct 3, 2025" (a
 * previous year). `now` null = server render (the page is ISR-cached, so the server must not use its clock): the
 * month-day only. A time in the future (clock skew) reads "just now".
 */
export function relativeTime(value: Date | string | null | undefined, now: Date | null): string | null {
    const d = toDate(value);
    if (!d) return null;
    if (!now) return formatMonthDay(d);
    const diff = now.getTime() - d.getTime();
    if (diff < MINUTE) return "just now";
    if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
    if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
    if (diff < 2 * DAY) return "yesterday";
    const days = Math.floor(diff / DAY);
    if (days <= RELATIVE_MAX_DAYS) return `${days} days ago`;
    return d.getUTCFullYear() === now.getUTCFullYear() ? formatMonthDay(d) : formatHistoryDate(d);
}

// ---------------------------------------------------------------------------------------------------------------
// Panel: stats + activity timeline
// ---------------------------------------------------------------------------------------------------------------

export interface ActivityStats {
    /** Human uses in the last USAGE_WINDOW_DAYS days: one per shopper per day (a reveal and a copy are one use). */
    uses: number;
    /** Conclusive checker runs (all time) + valid shopper reports (180 days) + the merchant confirmation (once). */
    verifications: number;
    /** Newest human use (ISO), null without one. */
    lastUsedAt: string | null;
}

export type ActivityEvent =
    | {
        kind: "check";
        /** Newest run of the entry (ISO). */
        at: string;
        outcome: HistoryOutcome;
        /** Runs with this outcome on this UTC day, collapsed into one entry. */
        count: number;
    }
    | {
        kind: "report";
        at: string;
        outcome: "worked" | "failed";
        /** Public short name of a signed-in user ("Maria P."), null = anonymous ("A shopper"). */
        actor: string | null;
        /** Anonymous reports with this outcome on this UTC day, collapsed into one entry ("3 shoppers"); 1 for a named one. */
        count: number;
    }
    | {
        /** The merchant confirmed the offer (Coupon.protectedFromExpiry): at most one per coupon. */
        kind: "confirmed";
        /** Protection date (ISO); null = unknown, shown without a time and sorted last. */
        at: string | null;
        outcome: "worked";
        /** Merchant domain ("contabo.com"); null = not known ("Confirmed by the merchant"). */
        domain: string | null;
    };

export interface CouponActivity {
    couponId: string;
    stats: ActivityStats;
    /** Newest first, at most ACTIVITY_MAX. */
    events: ActivityEvent[];
}

/** One valid shopper report (`valid-votes.ts`), with the signed-in user's id when there is one. */
export interface ReportRow {
    type: string; // "UP" | "DOWN"
    createdAt: Date | string;
    userId?: string | null;
}

/** One human click on the code (`human-clicks.ts` already dropped the sweeper sessions). */
export interface UsageRow {
    userId?: string | null;
    sessionId?: string | null;
    createdAt: Date | string;
}

/**
 * The public short form of a user's name, exactly as the community leaderboard shows it: first word + initial of the
 * second ("Maria Popescu" -> "Maria P."), or the single word. Null (-> "A shopper") for no name, an e-mail-like or
 * link-like name, or anything without a letter.
 */
export function publicUserName(name: string | null | undefined): string | null {
    if (!name) return null;
    const clean = name.replace(/\s+/g, " ").trim();
    if (!clean || /[@/<>]|https?:|www\./i.test(clean) || !/\p{L}/u.test(clean)) return null;
    const parts = clean.split(" ");
    const first = parts[0].slice(0, 24);
    const initial = parts.length > 1 ? Array.from(parts[1])[0] : null;
    return initial ? `${first} ${initial.toUpperCase()}.` : first;
}

function usageIdentity(row: UsageRow): string | null {
    if (row.userId) return `u:${row.userId}`;
    if (row.sessionId) return `s:${row.sessionId}`;
    return null;
}

/** The merchant confirmation of the coupon (Coupon.protectedFromExpiry), resolved by the database side. */
export interface ConfirmationInput {
    /** Coupon.protectedAt; null/absent = unknown. */
    at?: Date | string | null;
    /** The store's registrable domain; null/absent = unknown. */
    domain?: string | null;
}

/** "contabo.com" from a stored domain or hostname: lowercase, no scheme, no "www.", no path; null when nothing usable. */
export function cleanMerchantDomain(value: string | null | undefined): string | null {
    if (!value) return null;
    const host = value.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "").split(/[/?#]/)[0].replace(/^www\./, "");
    return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host) ? host : null;
}

export interface BuildActivityInput {
    couponId: string;
    /** Runs of the coupon, any order; inconclusive ones are dropped here. */
    runs: RunRow[];
    /** Total conclusive runs when `runs` was truncated by the query (>= conclusive runs given). */
    runTotal?: number;
    /** Valid shopper reports (after the bot filter). */
    reports: ReportRow[];
    /** Human clicks of the last 180 days. */
    usage: UsageRow[];
    /** userId -> name of the users behind the reports (only users that exist; role filter applied by the caller). */
    names?: Map<string, string | null>;
    /** Present when the offer is merchant-confirmed (CODE or DEAL): one "Confirmed on {domain}" entry. */
    confirmation?: ConfirmationInput | null;
    limit?: number;
}

export function buildCouponActivity(input: BuildActivityInput): CouponActivity {
    const checks: Array<{ at: string; outcome: HistoryOutcome }> = [];
    for (const run of input.runs) {
        const outcome = runOutcome(run);
        const when = toDate(run.createdAt);
        if (outcome && when) checks.push({ at: when.toISOString(), outcome });
    }
    checks.sort((a, b) => b.at.localeCompare(a.at));

    // Same UTC day + same outcome, next to each other: one entry "tested at checkout 3 times".
    const events: ActivityEvent[] = [];
    for (const c of checks) {
        const prev = events[events.length - 1];
        if (prev && prev.kind === "check" && prev.outcome === c.outcome && prev.at.slice(0, 10) === c.at.slice(0, 10)) prev.count++;
        else events.push({ kind: "check", at: c.at, outcome: c.outcome, count: 1 });
    }

    const reports: Array<{ at: string; outcome: "worked" | "failed"; actor: string | null }> = [];
    for (const r of input.reports) {
        const when = toDate(r.createdAt);
        if (!when || (r.type !== "UP" && r.type !== "DOWN")) continue;
        const actor = r.userId ? publicUserName(input.names?.get(r.userId)) : null;
        reports.push({ at: when.toISOString(), outcome: r.type === "UP" ? "worked" : "failed", actor });
    }
    reports.sort((a, b) => b.at.localeCompare(a.at));
    // Anonymous reports: same UTC day + same outcome, next to each other: one entry "3 shoppers reported it ...".
    // A named report always stands alone.
    const reportEvents: ActivityEvent[] = [];
    for (const r of reports) {
        const prev = reportEvents[reportEvents.length - 1];
        if (r.actor === null && prev && prev.kind === "report" && prev.actor === null && prev.outcome === r.outcome && prev.at.slice(0, 10) === r.at.slice(0, 10)) prev.count++;
        else reportEvents.push({ kind: "report", at: r.at, outcome: r.outcome, actor: r.actor, count: 1 });
    }
    events.push(...reportEvents);
    const reportCount = reports.length;

    const useKeys = new Set<string>();
    let lastUsed: string | null = null;
    for (const u of input.usage) {
        const when = toDate(u.createdAt);
        const who = usageIdentity(u);
        if (!when || !who) continue;
        const iso = when.toISOString();
        useKeys.add(`${who}|${iso.slice(0, 10)}`);
        if (!lastUsed || iso > lastUsed) lastUsed = iso;
    }

    // Stable: newest first, a check before a report at the same instant.
    const rank = (e: ActivityEvent) => (e.kind === "check" ? 0 : e.kind === "confirmed" ? 1 : 2);
    events.sort((a, b) => (b.at ?? "").localeCompare(a.at ?? "") || rank(a) - rank(b));

    // The merchant confirmation counts once, and always stays in the list (it is usually the oldest entry, so a plain
    // newest-20 cut would drop it). An unknown date sorts last.
    const limit = Math.max(0, input.limit ?? ACTIVITY_MAX);
    let shown = events.slice(0, limit);
    let confirmations = 0;
    if (input.confirmation) {
        confirmations = 1;
        const at = toDate(input.confirmation.at)?.toISOString() ?? null;
        const confirmed: ActivityEvent = { kind: "confirmed", at, outcome: "worked", domain: cleanMerchantDomain(input.confirmation.domain) };
        const others = limit > 0 ? events.slice(0, limit - 1) : [];
        shown = [...others, confirmed].sort((a, b) => (b.at ?? "").localeCompare(a.at ?? "") || rank(a) - rank(b));
        if (limit === 0) shown = [];
    }

    return {
        couponId: input.couponId,
        stats: {
            uses: useKeys.size,
            verifications: Math.max(input.runTotal ?? 0, checks.length) + reportCount + confirmations,
            lastUsedAt: lastUsed,
        },
        events: shown,
    };
}

const OUTCOME_WORDS: Record<HistoryOutcome, string> = {
    worked: "worked",
    failed: "didn't work",
    expired: "expired",
};

export function outcomeWords(outcome: HistoryOutcome): string {
    return OUTCOME_WORDS[outcome];
}

/** The words of one timeline entry, without its time. The actor is a mark, never a name in the row for our checks. */
export interface EventText {
    /** Row mark: the "CS" monogram for our checkout tests, a person glyph for shoppers, a check-shield for the merchant. */
    mark: "checkout" | "shopper" | "merchant";
    /** "Tested at checkout", "Shopper report", "3 shopper reports", "Maria P.", "Confirmed on contabo.com" */
    label: string;
    /** "worked" / "didn't work" / "expired"; "reported it worked" / "reported it didn't work" for a named shopper; "" for a confirmation (the label says it all). */
    outcome: string;
    /** "good" for worked, "bad" otherwise (styling only). */
    tone: "good" | "bad";
}

/**
 * Row text "{label} · {outcome}" (owner 2026-10-24: "CouponSwift" once in the legend, never in the rows):
 *   check:            "Tested at checkout · worked" / "Tested at checkout 3 times · didn't work"
 *   anonymous report: "Shopper report · didn't work" / "3 shopper reports · didn't work"
 *   named report:     "Maria P. · reported it worked"
 *   confirmation:     "Confirmed on contabo.com" (or "Confirmed by the merchant" when the domain is unknown), no outcome
 */
export function eventText(event: ActivityEvent): EventText {
    if (event.kind === "confirmed") {
        return { mark: "merchant", label: event.domain ? `Confirmed on ${event.domain}` : "Confirmed by the merchant", outcome: "", tone: "good" };
    }
    const tone = event.outcome === "worked" ? "good" : "bad";
    const words = outcomeWords(event.outcome);
    if (event.kind === "check") {
        return { mark: "checkout", label: event.count > 1 ? `Tested at checkout ${event.count} times` : "Tested at checkout", outcome: words, tone };
    }
    if (event.actor) return { mark: "shopper", label: event.actor, outcome: `reported it ${words}`, tone };
    return { mark: "shopper", label: event.count > 1 ? `${event.count} shopper reports` : "Shopper report", outcome: words, tone };
}

/**
 * The panel's one-line legend under "Verification activity": the only place our name appears. Names only the kinds the
 * timeline holds: "Checkout tests by CouponSwift, merchant confirmations and shopper reports". Null without events.
 */
export function activityLegend(events: ActivityEvent[]): string | null {
    const parts: string[] = [];
    if (events.some(e => e.kind === "check")) parts.push(`Checkout tests by ${VERIFIER_DISPLAY_NAME}`);
    if (events.some(e => e.kind === "confirmed")) parts.push("merchant confirmations");
    if (events.some(e => e.kind === "report")) parts.push("shopper reports");
    if (parts.length === 0) return null;
    const text = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The small note under the "Verifications" tile: what the number counts. */
export function verificationsNote(events: ActivityEvent[]): string {
    if (!events.some(e => e.kind === "confirmed")) return "checks + shopper reports";
    const parts: string[] = [];
    if (events.some(e => e.kind === "check")) parts.push("checks");
    parts.push("confirmation");
    if (events.some(e => e.kind === "report")) parts.push("shopper reports");
    return parts.join(" + ");
}
